import _ from 'lodash'
import { PropsWithChildren, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Box, Button, Card, CardHeader, Checkbox, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

type ListOption = {
  label: string
  value: string
}

type SortableListOption = ListOption & { id: string }

type TransferListProps = {
  initialLeftOptions: ListOption[]
  initialRightOptions: ListOption[]
  onChange?: (options: ListOption[]) => void
  readOnly?: boolean
  sortable?: boolean
}

function SortableListItem(props: PropsWithChildren<{ item: { label: string; value: string; id: string } }>) {
  const { children, item } = props
  const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: item.id,
  })

  return (
    <Box
      ref={setNodeRef}
      sx={[
        { position: 'relative' },
        !!transform && { transform: CSS.Translate.toString(transform) },
        !!transition && { transition },
        isDragging && { zIndex: 10 },
      ]}
    >
      <ListItem
        key={item.value}
        role="listitem"
        disablePadding
        secondaryAction={
          <IconButton edge="end" aria-label="drag" ref={setActivatorNodeRef} {...attributes} {...listeners}>
            <DragIndicatorIcon />
          </IconButton>
        }
      >
        {children}
      </ListItem>
    </Box>
  )
}

export default function TransferList(props: TransferListProps) {
  const { initialLeftOptions, initialRightOptions, onChange, readOnly = false, sortable = false } = props
  const { t } = useTranslation()
  const sensors = useSensors(useSensor(PointerSensor))

  const [checked, setChecked] = useState<ListOption[]>([])
  const [left, setLeft] = useState<ListOption[]>(_.differenceBy(initialLeftOptions, initialRightOptions, 'value'))
  const [right, setRight] = useState<ListOption[]>(initialRightOptions)

  const sortableRight = useMemo(() => _.map(right, ({ label, value }) => ({ label, value, id: value })), [right])

  const leftChecked = _.intersectionBy(checked, left, 'value')
  const rightChecked = _.intersectionBy(checked, right, 'value')

  const handleToggle = (option: ListOption) => () => {
    if (readOnly) return

    const currentIndex = _.findIndex(checked, ['value', option.value])
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(option)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleAllRight = () => {
    const newRight = right.concat(left)
    setRight(newRight)
    setLeft([])
    onChange?.(newRight)
  }

  const handleCheckedRight = () => {
    const newRight = right.concat(leftChecked)
    setRight(newRight)
    setLeft(_.differenceBy(left, leftChecked, 'value'))
    setChecked(_.differenceBy(checked, leftChecked, 'value'))
    onChange?.(newRight)
  }

  const handleCheckedLeft = () => {
    const newLeft = left.concat(rightChecked)
    const newRight = _.differenceBy(right, rightChecked, 'value')
    setLeft(newLeft)
    setRight(newRight)
    setChecked(_.differenceBy(checked, rightChecked, 'value'))
    onChange?.(newRight)
  }

  const handleAllLeft = () => {
    setLeft(left.concat(right))
    setRight([])
    onChange?.([])
  }

  const customList = (title: ReactNode, items: (ListOption | SortableListOption)[], sortable = false) => (
    <Card>
      <CardHeader
        sx={{
          px: 2,
          py: 1,
          '& .MuiCardHeader-title': {
            fontWeight: 400,
            fontSize: '0.875rem',
          },
        }}
        title={title}
      />
      <Divider />
      <List sx={{ height: 230, bgcolor: 'background.paper', overflow: 'auto' }} dense component="div" role="list">
        {items.map((item) => {
          return sortable && !readOnly ? (
            <SortableListItem key={item.value} item={item as ListOption & { id: string }}>
              <ListItemButton sx={{ '&.Mui-disabled': { opacity: 1 } }} disabled={readOnly} onClick={handleToggle(item)}>
                {!readOnly && (
                  <ListItemIcon>
                    <Checkbox checked={_.findIndex(checked, ['value', item.value]) !== -1} tabIndex={-1} disableTouchRipple />
                  </ListItemIcon>
                )}
                <ListItemText primary={item.label} />
              </ListItemButton>
            </SortableListItem>
          ) : (
            <ListItem key={item.value} role="listitem" disablePadding>
              <ListItemButton sx={{ '&.Mui-disabled': { opacity: 1 } }} disabled={readOnly} onClick={handleToggle(item)}>
                {!readOnly && (
                  <ListItemIcon>
                    <Checkbox checked={_.findIndex(checked, ['value', item.value]) !== -1} tabIndex={-1} disableTouchRipple />
                  </ListItemIcon>
                )}
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </Card>
  )

  return (
    <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
      <Box flex={1}>{customList(t('component.ui.transferList.choices'), left)}</Box>
      <Stack alignItems="center">
        <Button sx={{ my: 0.5 }} variant="outlined" size="small" onClick={handleAllRight} disabled={left.length === 0 || readOnly} aria-label="move all right">
          ≫
        </Button>
        <Button
          sx={{ my: 0.5 }}
          variant="outlined"
          size="small"
          onClick={handleCheckedRight}
          disabled={leftChecked.length === 0 || readOnly}
          aria-label="move selected right"
        >
          &gt;
        </Button>
        <Button
          sx={{ my: 0.5 }}
          variant="outlined"
          size="small"
          onClick={handleCheckedLeft}
          disabled={rightChecked.length === 0 || readOnly}
          aria-label="move selected left"
        >
          &lt;
        </Button>
        <Button sx={{ my: 0.5 }} variant="outlined" size="small" onClick={handleAllLeft} disabled={right.length === 0 || readOnly} aria-label="move all left">
          ≪
        </Button>
      </Stack>
      <Box flex={1}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          onDragEnd={(event) => {
            const { active, over } = event

            if (active.id !== over?.id) {
              const oldIndex = right.findIndex(({ value }) => value === active.id)
              const newIndex = right.findIndex(({ value }) => value === over?.id)

              const newRight = arrayMove(right, oldIndex, newIndex)
              setRight(newRight)
              onChange?.(newRight)
            }
          }}
        >
          <SortableContext items={sortableRight} strategy={verticalListSortingStrategy}>
            {customList(t('component.ui.transferList.chosen'), sortable ? sortableRight : right, sortable)}
          </SortableContext>
        </DndContext>
      </Box>
    </Stack>
  )
}
